// src/pages/Pickup.js
import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { useCart } from '../context/CartContext';

/* ---------- layout ---------- */
const Page = styled.div`
  max-width: 1100px;
  margin: 24px auto 32px;
  padding: 0 16px;
`;
const H1 = styled.h1`
  text-align: center;
  margin: 0 0 12px;
  font-family: 'Lobster', cursive;
`;
const Tabs = styled.div`
  display: flex; flex-wrap: wrap; gap: 10px; justify-content: center;
  margin-bottom: 16px;
`;
const Tab = styled.button`
  border: 1px solid #ff761a;
  background: transparent;
  color: #ff761a;
  border-radius: 999px;
  padding: 8px 14px;
  cursor: pointer;
  &.active { background: #ff761a; color: #fff; }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
`;
const Card = styled.div`
  border: 1px solid #eee;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 18px rgba(0,0,0,0.06);
  overflow: hidden;
`;
const Img = styled.img`
  width: 100%; height: 160px; object-fit: cover; background: #f5f5f5;
`;
const Body = styled.div` padding: 14px; `;
const Name = styled.h3` margin: 0 0 6px; font-size: 1.2rem; font-family: 'Lobster', cursive; `;
const Desc = styled.p` margin: 0 0 10px; color: #333; line-height: 1.5; `;
const Price = styled.div` color: #ff761a; font-weight: 700; `;
const AddBtn = styled.button`
  margin-top: 10px;
  background: #ff761a; color: #fff; border: none; border-radius: 999px; padding: 10px 14px;
  cursor: pointer;
`;
const CancelBtn = styled.button`
  margin-top: 10px;
  background: #bbb; color: #fff; border: none; border-radius: 999px; padding: 10px 14px;
  cursor: pointer;
`;

/* ---------- modal ---------- */
const Backdrop = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.6);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
  padding: 16px; /* gives breathing room on small screens */
`;
const Modal = styled.div`
  width: 92%;
  max-width: 720px;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 16px;
  /* critical: never exceed viewport height, layout with header/body/footer */
  max-height: 92vh;
  display: grid;
  grid-template-rows: auto 1fr auto;
  overflow: hidden; /* clip inner scroll area */
`;
const ModalTitle = styled.h3`
  margin: 0; padding: 18px 18px 8px 18px; /* move padding from Modal */
`;
const ScrollArea = styled.div`
  overflow-y: auto;
  padding: 0 18px 8px 18px;
  /* optional: softer scroll on iOS */
  -webkit-overflow-scrolling: touch;
`;
const Row = styled.div` margin: 12px 0; `;
const Actions = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 12px 18px 18px 18px;
  border-top: 1px solid #eee;
  background: #fff; /* ensures footer readable over scroll */
`;
const SecTitle = styled.div` font-weight: 700; margin-bottom: 6px; `;
const OptGroup = styled.div` display: flex; flex-wrap: wrap; gap: 10px; `;
const Chip = styled.label`
  display: inline-flex; align-items: center; gap: 6px;
  border: 1px solid #ddd; border-radius: 999px; padding: 6px 10px; cursor: pointer;
`;
const Pill = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  border: 1px solid #ddd; border-radius: 999px; padding: 6px 10px;
`;
const StepBtn = styled.button`
  border: 1px solid #ccc; background: #f8f8f8; border-radius: 6px; padding: 2px 8px; cursor: pointer;
`;
const Help = styled.div` font-size: 0.9rem; color: #666; `;
const ErrorText = styled.div` color: #c0392b; font-size: 0.9rem; margin-top: 4px; `;

/* ---------- static categories / initial state ---------- */
const categories = [
  { key: 'signature', label: 'Signature Bowls' },
  { key: 'cyo', label: 'Create-Your-Own Poké' },
  { key: 'hot', label: 'Hot Stuff' },
  { key: 'sides', label: 'Sides and Extras' },
];
const initialCatalog = { signature: [], cyo: [], hot: [], sides: [] };

/* ---------- helpers for dynamic groups ---------- */
const isMultiType = (type) => type === 'multi' || type === 'checkbox';
const isSingleType = (type) => type === 'single' || type === 'radio';
const displayPriceDelta = (d) => {
  const n = Number(d || 0);
  if (!n) return '';
  const sign = n >= 0 ? '+' : '-';
  return ` (${sign}$${Math.abs(n).toFixed(2)})`;
};

export default function Pickup() {
  const [tab, setTab] = useState('signature');
  const { addItem } = useCart();
  const cardsRef = useRef([]);
  const [catalog, setCatalog] = useState(initialCatalog);

  // modal state (dynamic)
  const [configOpen, setConfigOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [groups, setGroups] = useState([]); // from API
  const [qtyByGroup, setQtyByGroup] = useState({}); // { [groupId]: { [valueId]: number } }
  const [loadingOpts, setLoadingOpts] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (cardsRef.current.length) {
      gsap.set(cardsRef.current, { opacity: 0, y: 12 });
      gsap.to(cardsRef.current, { opacity: 1, y: 0, duration: 0.5, stagger: 0.07, ease: 'power2.out' });
    }
  }, [tab]);

  const items = catalog[tab] || [];

  const fetchItemOptions = useCallback(async (itemId) => {
    setLoadingOpts(true);
    setLoadError('');
    try {
      const r = await fetch(`/api/items/${itemId}/options`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();

      const init = {};
      for (const g of json) init[g.id] = {}; // all zero
      setGroups(json);
      setQtyByGroup(init);
    } catch (e) {
      setGroups([]);
      setQtyByGroup({});
      setLoadError('Failed to load options. Try again.');
    } finally {
      setLoadingOpts(false);
    }
  }, []);

  const openConfig = (item) => {
    setCurrentItem(item);
    setConfigOpen(true);
    fetchItemOptions(item.id);
  };

  const setValueQty = (groupId, valueId, nextQty) => {
    setQtyByGroup((prev) => {
      const g = { ...(prev[groupId] || {}) };
      if (nextQty <= 0) delete g[valueId]; else g[valueId] = nextQty;
      return { ...prev, [groupId]: g };
    });
  };

  const groupTotal = (groupId, map) => {
    const g = map[groupId] || {};
    return Object.values(g).reduce((a, b) => a + b, 0);
  };

  const computedPrice = useMemo(() => {
    if (!currentItem) return '0.00';
    let base = parseFloat(currentItem.price || 0);
    for (const g of groups) {
      const gm = qtyByGroup[g.id] || {};
      for (const v of (g.values || [])) {
        const q = gm[v.id] || 0;
        if (q) base += q * parseFloat(v.price_delta || 0);
      }
    }
    return base.toFixed(2);
  }, [currentItem, groups, qtyByGroup]);

  const groupErrors = useMemo(() => {
    const errs = {};
    for (const g of groups) {
      const min = Number(g.min) || 0;
      const max = Number(g.max) || 0;

      if (isSingleType(g.type)) {
        const total = groupTotal(g.id, qtyByGroup);
        if (min === 1 && total !== 1) errs[g.id] = 'Choose 1.';
        if (!min && total > 1) errs[g.id] = 'Choose exactly 1.';
        continue;
      }

      if (g.allow_duplicates) {
        const total = groupTotal(g.id, qtyByGroup);
        if (min && total < min) errs[g.id] = `Choose at least ${min}.`;
        if (max && total > max) errs[g.id] = `Choose at most ${max}.`;
        continue;
      }

      // multi without duplicates → count distinct values (0/1 each)
      const gm = qtyByGroup[g.id] || {};
      const distinct = Object.values(gm).filter((q) => q > 0).length;
      if (min && distinct < min) errs[g.id] = `Choose at least ${min}.`;
      if (max && distinct > max) errs[g.id] = `Choose at most ${max}.`;
    }
    return errs;
  }, [groups, qtyByGroup]);

  const hasErrors = Object.keys(groupErrors).length > 0;

  const summaryText = useMemo(() => {
    const parts = [];
    for (const g of groups) {
      const gm = qtyByGroup[g.id] || {};
      const chosen = (g.values || []).filter((v) => (gm[v.id] || 0) > 0);
      if (!chosen.length) continue;

      const label = g.display_name || g.name;
      const names = chosen.map((v) => {
        const q = gm[v.id];
        const delta = Number(v.price_delta || 0) * q;
        const deltaTxt = delta ? ` (+$${delta.toFixed(2)})` : '';
        return `${v.name}${q > 1 ? ` x${q}` : ''}${deltaTxt}`;
      }).join(', ');
      parts.push(`${label}: ${names}`);
    }
    return parts.join(' | ');
  }, [groups, qtyByGroup]);

  const addConfigured = () => {
    if (!currentItem || hasErrors) return;
    const nameWithSummary = summaryText ? `${currentItem.name} — ${summaryText}` : currentItem.name;
    addItem(nameWithSummary, computedPrice);
    setConfigOpen(false);
  };

  const priceDeltaText = (d) => displayPriceDelta(d);

  return (
    <Page>
      <H1>Pickup — Build Your Order</H1>
      <FetchCatalog onLoaded={setCatalog} />
      <Tabs>
        {categories.map((c) => (
          <Tab key={c.key} className={tab === c.key ? 'active' : ''} onClick={() => setTab(c.key)}>
            {c.label}
          </Tab>
        ))}
      </Tabs>

      <Grid>
        {items.map((it, idx) => (
          <Card ref={(el) => (cardsRef.current[idx] = el)} key={`${it.id}-${it.name}`}>
            <Img src={it.image} alt={it.name} />
            <Body>
              <Name>{it.name}</Name>
              <Desc>{it.desc}</Desc>
              <Price>${parseFloat(it.price).toFixed(2)}</Price>
              <AddBtn onClick={() => openConfig(it)}>Add</AddBtn>
            </Body>
          </Card>
        ))}
      </Grid>

      {configOpen && (
        <Backdrop onClick={() => setConfigOpen(false)} aria-hidden>
          <Modal role="dialog" aria-modal="true" aria-label="Configure Item" onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{currentItem?.name}</ModalTitle>

            <ScrollArea>
              {loadingOpts && <Help>Loading options…</Help>}
              {loadError && <ErrorText>{loadError}</ErrorText>}
              {!loadingOpts && !loadError && groups.length === 0 && <Help>No options for this item.</Help>}

              {!loadingOpts && !loadError && groups.map((g) => {
                const min = Number(g.min) || 0;
                const max = Number(g.max) || 0;
                const total = groupTotal(g.id, qtyByGroup);
                const help = [
                  isSingleType(g.type) ? 'Choose 1' : null,
                  !isSingleType(g.type) && min ? `min ${min}` : null,
                  !isSingleType(g.type) && max ? `max ${max}` : null,
                  total ? `chosen ${total}` : null,
                ].filter(Boolean).join(' · ');

                return (
                  <Row key={g.id}>
                    <SecTitle>{g.display_name || g.name}</SecTitle>
                    {help && <Help>{help}</Help>}
                    <OptGroup>
                      {(g.values || []).map((v) => {
                        const gm = qtyByGroup[g.id] || {};
                        const q = gm[v.id] || 0;

                        // single → radios (0/1)
                        if (isSingleType(g.type)) {
                          return (
                            <Chip key={v.id}>
                              <input
                                type="radio"
                                name={`g-${g.id}`}
                                checked={q === 1}
                                onChange={() => setValueQty(g.id, v.id, 1)}
                              />
                              <span>{v.name}{priceDeltaText(v.price_delta)}</span>
                            </Chip>
                          );
                        }

                        // multi with duplicates → stepper
                        if (g.allow_duplicates) {
                          const nextTotalInc = total + 1;
                          const vCap = Number(v.max_qty || 0); // 0 = unlimited
                          const canInc = (!max || nextTotalInc <= max) && (!vCap || q + 1 <= vCap);
                          const canDec = q > 0;

                          return (
                            <Pill key={v.id}>
                              <span>{v.name}{priceDeltaText(v.price_delta)}</span>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <StepBtn type="button" onClick={() => setValueQty(g.id, v.id, q - 1)} disabled={!canDec}>−</StepBtn>
                                <span>{q}</span>
                                <StepBtn type="button" onClick={() => canInc && setValueQty(g.id, v.id, q + 1)} disabled={!canInc}>+</StepBtn>
                              </div>
                            </Pill>
                          );
                        }

                        // multi without duplicates → checkbox (qty 0/1)
                        const checked = q > 0;
                        return (
                          <Chip key={v.id}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setValueQty(g.id, v.id, checked ? 0 : 1)}
                            />
                            <span>{v.name}{priceDeltaText(v.price_delta)}</span>
                          </Chip>
                        );
                      })}
                    </OptGroup>
                    {groupErrors[g.id] && <ErrorText>{groupErrors[g.id]}</ErrorText>}
                  </Row>
                );
              })}
            </ScrollArea>

            <Actions>
              <CancelBtn onClick={() => setConfigOpen(false)}>Cancel</CancelBtn>
              <AddBtn onClick={addConfigured} disabled={hasErrors || loadingOpts}>
                Add ${computedPrice}
              </AddBtn>
            </Actions>
          </Modal>
        </Backdrop>
      )}
    </Page>
  );
}

/* ---------- fetch catalog ---------- */
function FetchCatalog({ onLoaded }) {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/pickup/categories');
        if (!r.ok) return;
        const json = await r.json();
        if (!cancelled) onLoaded(json);
      } catch (_) { }
    })();
    return () => { cancelled = true; };
  }, [onLoaded]);
  return null;
}
